// components/DriverFormModal.tsx
import React, { useState } from "react";
import Modal from "Common/Components/Modal";
import Select from "react-select";
import { X, ArrowDown } from "lucide-react";

interface FormField {
  label: string;
  name: string;
  type: string;
  placeholder: string;
  colSpan?: number;
}

interface DriverFormModalProps {
  show: boolean;
  isEdit: boolean;
  onHide: () => void;
  onSubmit: (e: React.MouseEvent<HTMLButtonElement>) => void;
  validation: any;
  formFields: FormField[];
  carsList: any[];
  selectedCarName: any;
  setSelectedCarName: (car: any) => void;
  searchInput: string;
  setSearchInput: (input: string) => void;
  plateData: {
    region: string;
    numeric: string;
    letter: string;
    ending: string;
  };
  handleRegionChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleNumericChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleEndingChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleLetterSelect: (letter: string) => void;
}

const DriverFormModal: React.FC<DriverFormModalProps> = ({
  show,
  isEdit,
  onHide,
  onSubmit,
  validation,
  formFields,
  carsList,
  selectedCarName,
  setSelectedCarName,
  searchInput,
  setSearchInput,
  plateData,
  handleRegionChange,
  handleNumericChange,
  handleEndingChange,
  handleLetterSelect,
}) => {
  const [showModalLeter, setShowModalLeter] = useState(false);

  const persianLetters = [
    "الف",
    "ب",
    "پ",
    "ت",
    "ث",
    "ج",
    "چ",
    "ح",
    "خ",
    "د",
    "ذ",
    "ر",
    "ز",
    "ژ",
    "س",
    "ش",
    "ص",
    "ض",
    "ط",
    "ظ",
    "ع",
    "غ",
    "ف",
    "ق",
    "ک",
    "گ",
    "ل",
    "م",
    "ن",
    "و",
    "ه",
    "ی",
  ];

  return (
    <Modal
      show={show}
      onHide={onHide}
      modal-center="true"
      className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
      dialogClassName="w-screen md:w-[50rem] bg-white shadow rounded-md dark:bg-zink-600"
    >
      <Modal.Header
        className="flex items-center justify-between p-4 border-b dark:border-zink-500"
        closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500"
      >
        <Modal.Title className="text-16">
          {isEdit ? "به روز رسانی اطلاعات" : "مشخصات راننده :"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
        <form
          className="create-form"
          id="create-form"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(e as any);
          }}
        >
          <div className="grid grid-cols-12 gap-4">
            {formFields.map((field: FormField, index: number) => (
              <div
                key={index}
                className={`col-span-12 flex gap-4 items-center lg:col-span-${
                  field.colSpan || 6
                } text-gray-800`}
              >
                <label className="inline-block whitespace-nowrap dark:text-white mr-1 mb-2 text-base font-medium">
                  {field.label}
                </label>
                <input
                  type={field.type}
                  className="form-input w-full placeholder-gray-400 placeholder:text-[12px]"
                  placeholder={field.placeholder}
                  name={field.name}
                  autoComplete="off"
                  onChange={validation.handleChange}
                  value={
                    validation.values[
                      field.name as keyof typeof validation.values
                    ] || ""
                  }
                />

                {validation.touched[
                  field.name as keyof typeof validation.touched
                ] &&
                  typeof validation.errors[
                    field.name as keyof typeof validation.errors
                  ] === "string" && (
                    <p className="text-red-400 text-sm mt-1">
                      {String(
                        validation.errors[
                          field.name as keyof typeof validation.errors
                        ]
                      )}
                    </p>
                  )}
              </div>
            ))}

            <div className="col-span-12 flex gap-4 items-center lg:col-span-6">
              <label className="text-base font-medium mb-2 dark:text-white">
                خودرو:
              </label>
              <Select
                options={carsList.map((car: any) => ({
                  value: car.id,
                  label: car.name,
                  data: car,
                }))}
                value={
                  selectedCarName
                    ? {
                        value: selectedCarName.id,
                        label: selectedCarName.name,
                        data: selectedCarName,
                      }
                    : null
                }
                onChange={(selectedOption: any) => {
                  setSelectedCarName(selectedOption?.data || null);
                }}
                onInputChange={setSearchInput}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: "44px",
                    borderColor: state.isFocused ? "#4CAF50" : "#ddd",
                    boxShadow: state.isFocused ? "0 0 0 1px #4CAF50" : "none",
                    "&:hover": {
                      borderColor: state.isFocused ? "#4CAF50" : "#ccc",
                    },
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                menuPortalTarget={document.body}
                placeholder=" نوع خودرو را انتخاب کنید..."
                noOptionsMessage={() => " نوع خودرو را این مشخصات یافت نشد"}
                loadingMessage={() => "در حال بارگذاری..."}
                className="w-full my-react-select-container"
                classNamePrefix="my-react-select"
                maxMenuHeight={300}
                isSearchable
                closeMenuOnSelect={true}
              />
            </div>

            <div className="col-span-12 flex flex-col">
              <label className="inline-block whitespace-nowrap dark:text-white mr-1 mb-2 text-base font-medium">
                شماره پلاک:
              </label>

              <div className="flex items-center">
                <div className="relative ml-2">
                  <input
                    type="text"
                    value={plateData.region}
                    onChange={handleRegionChange}
                    placeholder="64"
                    className="w-16 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-lg font-bold"
                    maxLength={2}
                    dir="ltr"
                  />
                </div>

                <div className="flex items-center justify-center w-20 px-2 py-2 bg-gray-100 rounded-md text-lg font-bold">
                  ایران
                </div>

                <div className="relative">
                  <input
                    type="text"
                    value={plateData.numeric}
                    onChange={handleNumericChange}
                    placeholder="268"
                    className="w-20 px-3 mx-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-lg font-bold"
                    maxLength={3}
                    dir="ltr"
                  />
                </div>

                <div className="relative">
                  <div
                    onClick={() => setShowModalLeter(true)}
                    className="w-16 px-2 py-2 border mx-2 border-gray-300 rounded-md cursor-pointer 
                             focus:outline-none focus:ring-2 focus:ring-blue-500 text-center 
                             text-lg font-bold bg-white flex items-center justify-between"
                  >
                    <span>{plateData.letter}</span>
                    <ArrowDown className="text-blue-600" />
                  </div>

                  {showModalLeter && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                      <div className="bg-white rounded-lg p-6 w-80 max-w-sm">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-bold">انتخاب حرف</h3>
                          <button
                            onClick={() => setShowModalLeter(false)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <X />
                          </button>
                        </div>
                        <div className="grid grid-cols-6 gap-2">
                          {persianLetters.map((letter) => (
                            <button
                              key={letter}
                              onClick={() => {
                                handleLetterSelect(letter);
                                setShowModalLeter(false);
                              }}
                              className={`w-10 h-10 flex items-center justify-center rounded-lg text-lg font-bold
                                        transition-all duration-200 ${
                                          plateData.letter === letter
                                            ? "bg-blue-500 text-white shadow-md"
                                            : "bg-gray-100 text-gray-700 hover:bg-blue-100 hover:text-blue-600"
                                        }`}
                            >
                              {letter}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="relative">
                  <input
                    type="text"
                    value={plateData.ending}
                    onChange={handleEndingChange}
                    placeholder="41"
                    className="w-16 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-lg font-bold"
                    maxLength={2}
                    dir="ltr"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer className="flex justify-center p-4 border-t dark:border-zink-500">
        <button
          type="button"
          onClick={onSubmit}
          className="px-4 py-2 mt-4 font-bold text-white bg-blue-500 rounded hover:bg-blue-600"
        >
          {isEdit ? "بروز رسانی" : "ثبت اطلاعات"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default DriverFormModal;
